/**
 * Funding Marketplace — types and the shared partner cache.
 *
 * There is no partner list in this file on purpose. The companies and their
 * questions are defined once, in app/services/funding_partners.py, and read
 * from GET /funding/partners. Adding a company is a backend-only edit; the
 * sidebar entry and the form page appear on their own.
 */

import { api } from "@/lib/api";

export interface FieldOption {
  value: string;
  label: string;
}

/** One question. `type` drives which control FundingApply renders. */
export interface PartnerField {
  key: string;
  label: string;
  type: "text" | "email" | "tel" | "url" | "textarea" | "number" | "money" | "radio" | "consent";
  required: boolean;
  options: FieldOption[];
  placeholder: string | null;
  help: string | null;
  min: number | null;
  max: number | null;
  /** "name" | "email" | "phone" — prefilled from the signed-in account. */
  role: string | null;
  /** Prefilled value when no role applies. The buyer can still change it. */
  default: string | null;
}

/** A confirmed fact about a lender, e.g. { label: "Loan size", value: "$350K–$5M" }. */
export interface PartnerFact {
  label: string;
  value: string;
}

export interface FundingPartner {
  slug: string;
  name: string;
  tagline: string;
  intro: string;
  /** Paragraph on the marketplace card — who the lender is, what they finance. */
  blurb: string;
  /** Short claims listed on the card. */
  highlights: string[];
  footnote: string | null;
  tags: string[];
  facts: PartnerFact[];
  website: string | null;
  logo_url: string | null;
  /** What happens after submitting, in the lender's own terms. */
  next_steps: string | null;
  fields: PartnerField[];
}

export interface FundingApplication {
  id: string;
  reference_code: string;
  partner_slug: string;
  /** Resolved server-side, so it survives the lender being deactivated. */
  partner_name: string;
  status: string;
  /** When staff last moved the status; null while it is still "submitted". */
  status_changed_at: string | null;
  listing_id: string | null;
  listing_title: string | null;
  answers: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

/**
 * Where a submission stands, as the buyer sees it.
 *
 * Everything lands on `submitted`; staff move it along from the admin queue at
 * /admin/funding-applications.
 */
export const APPLICATION_STATUSES = ["submitted", "in_review", "approved", "rejected"] as const;

const STATUS_LABELS: Record<string, string> = {
  submitted: "Submitted",
  in_review: "In review",
  approved: "Approved",
  rejected: "Rejected",
};

export const statusLabel = (v: string) => STATUS_LABELS[v] ?? v;

/** One line under the status chip, so "Submitted" never reads as "ignored". */
export const STATUS_HINT: Record<string, string> = {
  submitted: "Sent to the lender. They contact you directly by email or phone.",
  in_review: "The lender is reviewing your packet.",
  approved: "Pre-qualified. The lender will be in touch about next steps.",
  rejected: "Not a fit for this lender right now.",
};

/** Semantic status colors, translucent so they read on the dark theme. */
export const STATUS_STYLE: Record<string, string> = {
  submitted: "border-primary/40 bg-primary/10 text-primary",
  in_review: "border-sky-500/40 bg-sky-500/10 text-sky-600 dark:text-sky-400",
  approved: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  rejected: "border-rose-500/40 bg-rose-500/10 text-rose-600 dark:text-rose-400",
};

/**
 * One shared, cached fetch of the partner list.
 *
 * Both the sidebar and the form page need it, and the sidebar mounts on every
 * navigation — a request per mount would put a call on every page change for a
 * value that changes when someone deploys. Same posture as lib/moduleVisibility.
 *
 * Only a SUCCESSFUL response is cached. A failure rejects and is retried on the
 * next call — caching it would show "no lenders" until a full page reload.
 * Callers decide how to fail: the sidebar collapses the group silently, the
 * pages show a retry.
 *
 * No reset-on-sign-out, unlike the module-visibility store: the partner list
 * is a code registry, identical for every viewer, so a cache surviving a
 * sign-out shows the next account exactly what it would have fetched.
 */
let cache: FundingPartner[] | null = null;
let inflight: Promise<FundingPartner[]> | null = null;

/** Drop the cached list and fetch it again — for when the server says the form
    the page is holding is out of date (a question was added since it loaded). */
export function refreshFundingPartners(): Promise<FundingPartner[]> {
  cache = null;
  return loadFundingPartners();
}

export function loadFundingPartners(): Promise<FundingPartner[]> {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = api
      .get<FundingPartner[]>("/funding/partners")
      .then((list) => {
        cache = list;
        return list;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

/* ── Formatters ──────────────────────────────────────────────────────────── */

export const usd = (v: string | number | null | undefined, fallback = "—") => {
  if (v === null || v === undefined || v === "") return fallback;
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
};

export function fmtDate(s: string) {
  return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export function fmtDateTime(s: string) {
  return new Date(s).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
